import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSiteContent } from "@/context/SiteContentContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/studio", label: "Programs" },
  { to: "/shop", label: "Gallery Shop" },
  { to: "/visit", label: "Contact Us" },
];

const useNavItems = () => {
  const { nav } = useSiteContent();
  const items = nav && nav.length ? nav : NAV;
  return items.filter((n) => n.visible !== false);
};

const navTid = (label) => `nav-${(label || "").toLowerCase().replace(/\s+/g, "-")}`;

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();
  const onHome = location.pathname === "/";
  const navItems = useNavItems();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = true; // heroes are light/ivory or use an ivory header bar

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-ivory/90 backdrop-blur-md border-b border-matte/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className={`flex items-center gap-3 ${solid ? "text-matte" : "text-ivory"}`}
        >
          <img
            src="/brainwaves-mark.png"
            alt="Brainwaves"
            className="h-11 w-auto object-contain"
          />
          <span className="font-serif text-2xl tracking-wide-sm leading-none">Brainwaves</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((n) => {
            const cls = `text-[12px] uppercase tracking-wide-sm link-underline transition-colors duration-300 ${
              solid ? "text-matte/80 hover:text-matte" : "text-ivory/90 hover:text-ivory"
            }`;
            return n.external ? (
              <a key={n.id || n.to} href={n.to} target="_blank" rel="noreferrer" className={cls} data-testid={navTid(n.label)}>
                {n.label}
              </a>
            ) : (
              <NavLink key={n.id || n.to} to={n.to} data-testid={navTid(n.label)} className={cls}>
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-5">
          <Link
            to="/cart"
            data-testid="cart-link"
            className={`relative ${solid ? "text-matte" : "text-ivory"}`}
          >
            <ShoppingBag size={20} strokeWidth={1.4} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-plum text-ivory text-[10px] h-4 min-w-4 px-1 flex items-center justify-center rounded-full"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            className={`lg:hidden ${solid ? "text-matte" : "text-ivory"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-ivory border-t border-matte/10 px-6 py-6 flex flex-col gap-5">
          {navItems.map((n) => n.external ? (
            <a key={n.id || n.to} href={n.to} target="_blank" rel="noreferrer" className="text-sm uppercase tracking-wide-sm text-matte/80" data-testid={`mobile-${navTid(n.label)}`}>
              {n.label}
            </a>
          ) : (
            <NavLink key={n.id || n.to} to={n.to} className="text-sm uppercase tracking-wide-sm text-matte/80" data-testid={`mobile-${navTid(n.label)}`}>
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { c } = useSiteContent();
  const navItems = useNavItems();
  const copyright = c("footer_copyright", "© {year} Brainwaves · Studio for Art & Science").replace("{year}", new Date().getFullYear());
  return (
    <footer className="bg-matte text-ivory/80 mt-32" data-testid="site-footer">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brainwaves-mark.png" alt="Brainwaves" className="h-14 w-auto object-contain" />
            <span className="font-serif text-3xl text-ivory">Brainwaves</span>
          </div>
          {c("footer_tagline") && (
            <p className="font-serif text-2xl text-ivory/70 max-w-md leading-snug">
              {c("footer_tagline")}
            </p>
          )}
        </div>
        <div className="md:col-span-3 md:col-start-8">
          <p className="text-[11px] uppercase tracking-editorial text-brass mb-5">{c("footer_explore_label", "Explore")}</p>
          <ul className="space-y-3 text-sm">
            {navItems.map((n) => (
              <li key={n.id || n.to}>
                {n.external ? (
                  <a href={n.to} target="_blank" rel="noreferrer" className="link-underline text-ivory/70 hover:text-ivory">{n.label}</a>
                ) : (
                  <Link to={n.to} className="link-underline text-ivory/70 hover:text-ivory">{n.label}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-[11px] uppercase tracking-editorial text-brass mb-5">{c("footer_studio_label", "Studio")}</p>
          <ul className="space-y-3 text-sm text-ivory/70">
            {c("footer_address") && <li>{c("footer_address")}</li>}
            {c("footer_hours") && <li>{c("footer_hours")}</li>}
            {c("footer_email") && <li>{c("footer_email")}</li>}
            <li>
              <Link to="/admin/login" className="link-underline" data-testid="admin-footer-link">
                Admin
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-ivory/10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-3 text-[11px] uppercase tracking-wide-sm text-ivory/40">
          <span>{copyright}</span>
          {c("footer_tagline_small") && <span>{c("footer_tagline_small")}</span>}
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <>
      <Header />
      <main className="relative z-10">
        <Outlet />
      </main>
      <Footer />
    </>
  );
}
